import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import { triggerPushPrompt } from './PushNotificationPrompt';

const FavoriteButton = ({ listingId, initialFavorited = false, size = 'default', className = '' }) => {
  const { user, token } = useAuth();
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if this listing is favorited
    if (user) {
      checkFavoriteStatus();
    }
  }, [user, listingId]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${listingId}`);
      setIsFavorited(response.data.is_favorited);
    } catch (error) {
      // Silently fail - not critical
    }
  };

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    setIsAnimating(true);

    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${listingId}`);
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { listing_id: listingId });
        setIsFavorited(true);
        toast.success('Added to favorites');
        // Trigger push notification prompt after adding to favorites
        triggerPushPrompt('favorite');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    } finally {
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const sizeClasses = {
    tiny: 'w-6 h-6',
    sm: 'w-8 h-8',
    small: 'w-7 h-7',
    default: 'w-10 h-10',
    large: 'w-12 h-12'
  };

  const iconSizes = {
    tiny: 'w-3 h-3',
    sm: 'w-4 h-4',
    small: 'w-3.5 h-3.5',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  // Use rounded-lg for sm size (sidebar), rounded-full for others
  const roundingClass = size === 'sm' ? 'rounded-lg' : 'rounded-full';

  return (
    <button
      onClick={toggleFavorite}
      className={`
        ${sizeClasses[size]} 
        ${roundingClass} flex items-center justify-center
        transition-all duration-300
        ${isFavorited 
          ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' 
          : size === 'sm' 
            ? 'bg-secondary text-muted-foreground hover:text-red-500'
            : 'bg-white/90 dark:bg-gray-800/90 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 shadow-md backdrop-blur-sm'
        }
        ${isAnimating ? 'scale-125' : 'scale-100'}
        ${className}
      `}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      data-testid="favorite-button"
    >
      <Heart 
        className={`
          ${iconSizes[size]} 
          transition-all duration-300
          ${isFavorited ? 'fill-current' : ''}
          ${isAnimating ? 'animate-ping' : ''}
        `} 
      />
    </button>
  );
};

export default FavoriteButton;
